import { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { User, Package, ArrowRight, MessageCircle, BookOpen, Copy, Check, RefreshCw, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

// Discord Verification Component
function DiscordVerification({ token, discordUrl }) {
  const [code, setCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const generateCode = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/discord/verification-code`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCode(response.data.code);
      toast.success('Verification code generated!');
    } catch (error) {
      toast.error('Failed to generate code');
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    if (code) {
      navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success('Code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <motion.div variants={fadeInUp} className="bg-[#5865F2] p-6 md:p-8 text-white">
      <div className="w-12 h-12 bg-[#0f0f0f]/20 rounded-full flex items-center justify-center mb-4">
        <MessageCircle className="w-6 h-6 text-white" />
      </div>
      <h2 className="font-serif text-xl mb-2">Join the Community</h2>
      <p className="text-white/80 text-sm mb-4">
        Connect with other members, get support, and stay accountable.
      </p>
      
      {/* Step 1: Join Discord */}
      <div className="bg-[#0f0f0f]/10 p-4 rounded mb-4">
        <p className="text-xs uppercase tracking-wider text-white/60 mb-2">Step 1</p>
        <a 
          href={discordUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-[#0f0f0f] text-[#5865F2] px-4 py-2 text-sm font-semibold hover:bg-[#0f0f0f]/90 transition-colors"
        >
          Join Discord Server
          <ArrowRight className="w-4 h-4" />
        </a>
      </div>

      {/* Step 2: Get Verification Code */}
      <div className="bg-[#0f0f0f]/10 p-4 rounded">
        <p className="text-xs uppercase tracking-wider text-white/60 mb-2">Step 2: Verify Your Access</p>
        {code ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <code className="bg-[#0f0f0f]/20 px-4 py-2 font-mono text-lg tracking-wider">
                {code}
              </code>
              <button
                onClick={copyCode}
                className="p-2 bg-[#0f0f0f]/20 hover:bg-[#0f0f0f]/30 transition-colors"
              >
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-white/70 text-xs">
              Enter this code in the <strong>#verify</strong> channel on Discord
            </p>
            <button
              onClick={generateCode}
              disabled={loading}
              className="text-xs text-white/60 hover:text-white flex items-center gap-1"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
              Generate new code
            </button>
          </div>
        ) : (
          <Button
            onClick={generateCode}
            disabled={loading}
            className="bg-[#0f0f0f] text-[#5865F2] hover:bg-[#0f0f0f]/90 font-semibold"
          >
            {loading ? 'Generating...' : 'Get Verification Code'}
          </Button>
        )}
      </div>
    </motion.div>
  );
}

export default function Account() {
  const { user, isAuthenticated, loading: authLoading, token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!token) return;
      
      try {
        const response = await axios.get(`${API}/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOrders(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchOrders();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, token]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-28">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  const hasOrders = orders.length > 0;
  const hasSubscription = orders.some(o => 
    o.items?.some(item => item.product_id === 'signal-accountability')
  );

  // Discord community link - update this when you create your server
  const DISCORD_INVITE_URL = "https://discord.gg/YOUR_INVITE_CODE";

  return (
    <div className="pt-28 bg-black min-h-screen" data-testid="account-page">
      {/* Header */}
      <section className="py-12 md:py-16 border-b border-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
              Account
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-serif text-3xl md:text-4xl lg:text-5xl text-white">
              Welcome, {user?.name || 'User'}
            </motion.h1>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12"
          >
            {/* Account Info & Quick Actions */}
            <div className="lg:col-span-1 space-y-6">
              {/* Account Details */}
              <motion.div variants={fadeInUp} className="bg-[#0f0f0f] border border-[#1a1a1a] p-6 md:p-8">
                <div className="w-16 h-16 bg-[#0f0f0f] rounded-full flex items-center justify-center mb-6">
                  <User className="w-8 h-8 text-white" />
                </div>
                <h2 className="font-serif text-xl text-white mb-4">Account Details</h2>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="text-[#999999]">Name:</span>
                    <p className="text-white font-medium">{user?.name}</p>
                  </div>
                  <div>
                    <span className="text-[#999999]">Email:</span>
                    <p className="text-white font-medium">{user?.email}</p>
                  </div>
                  <div>
                    <span className="text-[#999999]">Member Since:</span>
                    <p className="text-white font-medium">
                      {new Date(user?.created_at).toLocaleDateString('en-US', {
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Discord Community - Show for subscribers */}
              {hasSubscription && (
                <DiscordVerification token={token} discordUrl={DISCORD_INVITE_URL} />
              )}

              {/* Your Content - View Only */}
              {hasOrders && (
                <motion.div variants={fadeInUp} className="bg-[#0f0f0f] border border-[#1a1a1a] p-6 md:p-8">
                  <h2 className="font-serif text-xl text-white mb-4">Your Content</h2>
                  <div className="space-y-3">
                    {/* Guide Access - View Only */}
                    <Link to="/guide/read" className="block">
                      <Button 
                        className="w-full justify-start btn-premium bg-[#1A1A1A] text-white hover:bg-[#333333] rounded-none py-4"
                        data-testid="read-guide-link"
                      >
                        <BookOpen className="w-5 h-5 mr-3 text-[#D4AF37]" />
                        Read The Signal: Upgrade
                      </Button>
                    </Link>
                    {hasSubscription && (
                      <Link to="/contact" className="block">
                        <Button 
                          className="w-full justify-start btn-premium bg-[#D4AF37] text-white hover:bg-[#C9A030] rounded-none py-4"
                          data-testid="chat-support-link"
                        >
                          <MessageCircle className="w-5 h-5 mr-3" />
                          Chat Support
                        </Button>
                      </Link>
                    )}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Orders */}
            <div className="lg:col-span-2">
              <motion.h2 variants={fadeInUp} className="font-serif text-2xl text-white mb-6 flex items-center gap-3">
                <Package className="w-6 h-6 text-[#D4AF37]" />
                Your Orders
              </motion.h2>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="spinner" />
                </div>
              ) : orders.length === 0 ? (
                <motion.div variants={fadeInUp} className="bg-[#0f0f0f] border border-[#1a1a1a] p-8 text-center">
                  <Package className="w-12 h-12 text-[#E5E0D6] mx-auto mb-4" />
                  <h3 className="font-serif text-xl text-white mb-2">No Orders Yet</h3>
                  <p className="text-[#999999] mb-6">Start your transformation journey today.</p>
                  <Link to="/products">
                    <Button 
                      className="btn-premium bg-[#1A1A1A] text-white hover:bg-[#333333] rounded-none px-6 py-3 text-sm uppercase tracking-widest"
                      data-testid="browse-products-button"
                    >
                      Browse Products
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </motion.div>
              ) : (
                <motion.div variants={staggerContainer} className="space-y-4">
                  {orders.map((order) => (
                    <motion.div 
                      key={order.id}
                      variants={fadeInUp}
                      className="bg-[#0f0f0f] border border-[#1a1a1a] p-6"
                      data-testid={`order-${order.id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                        <div>
                          <p className="text-xs uppercase tracking-widest text-[#D4AF37] mb-1">
                            Order #{order.id?.slice(0, 8) || order._id?.slice(0, 8) || 'N/A'}
                          </p>
                          <p className="text-sm text-[#999999]">
                            {order.created_at ? new Date(order.created_at).toLocaleDateString('en-US', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            }) : 'Unknown date'}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="font-serif text-xl text-white">
                            €{(order.total || order.items?.reduce((sum, item) => sum + (item.price || 0), 0) || 0).toFixed(2)}
                          </span>
                          <span className={`px-3 py-1 text-xs uppercase tracking-wider ${
                            order.status === 'completed' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {order.status || 'pending'}
                          </span>
                        </div>
                      </div>

                      {/* Access Links */}
                      <div className="pt-4 border-t border-[#1a1a1a]">
                        <p className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                          <Download className="w-4 h-4 text-[#D4AF37]" />
                          Your Access
                        </p>
                        <div className="flex flex-wrap gap-3">
                          <Link to="/guide/read">
                            <Button 
                              variant="outline"
                              size="sm"
                              className="border-[#1a1a1a] text-[#999999] hover:bg-[#0f0f0f] rounded-none text-xs"
                            >
                              <BookOpen className="w-3 h-3 mr-2" />
                              Read The Signal
                            </Button>
                          </Link>
                          {order.download_urls?.map((download, idx) => (
                            <a
                              key={idx}
                              href={download.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Button 
                                variant="outline"
                                size="sm"
                                className="border-[#1a1a1a] text-[#999999] hover:bg-[#0f0f0f] rounded-none text-xs"
                              >
                                <Download className="w-3 h-3 mr-2" />
                                {download.product_name}
                              </Button>
                            </a>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
